<div class="bg-card rounded-2xl shadow-sm border border-gray-100 dark:border-foreground-tertiary w-full max-w-3xl overflow-hidden transition-all duration-300">
  <!-- Header -->
  <div class="p-6">
      <div class="flex justify-between items-center mb-4">
          <h2 class="text-xl font-bold text-foreground">Your Latest BMI</h2>
          <i class="pi pi-info-circle text-muted-color"></i>
      </div>

      <!-- BMI Value Section -->
      <div class="flex flex-col items-center justify-center">
          <h1 class="text-[32px] font-bold text-[#10B981] leading-none">{{ bmi | number:'1.1-2' }}</h1>

          <div class="flex items-center gap-1.5 mt-3">
              <svg *ngIf="isPositive" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EF4444" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline><polyline points="16 7 22 7 22 13"></polyline></svg>
              <svg *ngIf="!isPositive" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10B981" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline><polyline points="16 17 22 17 22 11"></polyline></svg>

              <span class="text-sm font-bold" [ngClass]="isPositive ? 'text-[#EF4444]' : 'text-[#10B981]'">
                  {{ isPositive ? '+' : '-' }}{{ change | number:'1.1-2' }} BMI
              </span>
              <span class="text-sm text-muted-color font-medium">vs last assessment</span>
          </div>

          <p class="text-xs text-muted-color opacity-70 mt-2 text-center">
              Updated on {{ date }} (Based on your latest assessment)
          </p>
      </div>

      <!-- BMI Scale -->
      <div class="mt-8 mb-2">
          <div class="relative h-3 rounded-full bg-gradient-to-r from-[#4ade80] via-[#facc15] to-[#ef4444]">
              <!-- Dynamic Indicator -->
              <div class="absolute top-[-5px] w-1 h-5.5 bg-gray-800 dark:bg-white rounded-sm shadow-sm transition-all duration-500 ease-out" 
                   [style.left]="indicatorPosition"
                   style="transform: translateX(-50%);">
              </div>
          </div>

          <!-- Scale Labels -->
          <div class="flex justify-between text-[11px] text-muted-color mt-3">
              <div class="flex flex-col items-start"><span class="font-bold text-gray-500"><18.5</span><span>Underweight</span></div>
              <div class="flex flex-col items-center"><span class="font-bold text-gray-500">18.5-24.9</span><span>Healthy</span></div>
              <div class="flex flex-col items-center"><span class="font-bold text-gray-500">25.0-29.9</span><span>Overweight</span></div>
              <div class="flex flex-col items-end"><span class="font-bold text-gray-500">>30</span><span>Obese</span></div>
          </div>
      </div>
  </div>

  <!-- Toggle Strip -->
  <div class="border-t border-gray-100 dark:border-foreground-tertiary">
      <button (click)="toggleExpand()" class="w-full flex justify-between items-center px-6 py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors focus:outline-none">
          <span class="text-sm font-semibold text-foreground">View Weight Insight</span>
          <i class="pi" [ngClass]="isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'" style="color: #F5A623; font-size: 1.2rem; transition: transform 0.3s ease"></i>
      </button>
  </div>

  <!-- Expanded Content -->
  <div *ngIf="isExpanded" class="px-6 pb-6 pt-2 animate-fadein">
      
      <div class="flex items-center mb-4">
          <!-- Weight -->
          <div class="flex-1 flex items-center gap-4">
              <div class="w-12 h-12 rounded-xl bg-[#E8F4FD] dark:bg-[#E8F4FD]/10 flex items-center justify-center text-[#3B9EDA]">
                  <i class="pi pi-briefcase" style="font-size: 1.5rem"></i>
              </div>
              <div class="flex flex-col">
                  <span class="text-base font-bold text-foreground">{{ weight | number:'1.0-1' }} kg</span>
                  <span class="text-[12px] text-muted-color">Current Weight</span>
              </div>
          </div>
          
          <div class="w-px h-10 bg-gray-200 dark:bg-foreground-tertiary mx-4"></div>
          
          <!-- Height -->
          <div class="flex-1 flex items-center gap-4">
              <div class="w-12 h-12 rounded-xl bg-[#FFF3E0] dark:bg-[#FFF3E0]/10 flex items-center justify-center text-[#F5A623]">
                  <i class="pi pi-arrows-v" style="font-size: 1.5rem"></i>
              </div>
              <div class="flex flex-col">
                  <span class="text-base font-bold text-foreground">{{ height | number:'1.0-1' }} cm</span>
                  <span class="text-[12px] text-muted-color">Height</span>
              </div>
          </div>
      </div>

      <!-- Footer Progress detail -->
      <div class="flex items-center gap-2 mt-4 bg-gray-50 dark:bg-gray-800/50 p-3 rounded-lg">
          <svg *ngIf="isPositive" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-red-500" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>
          <svg *ngIf="!isPositive" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" class="text-green-500" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>
          <span class="text-[13px] font-medium text-muted-color">Progress:</span>
          <span class="text-[13px] font-semibold text-foreground">{{ isPositive ? '+' : '-' }}{{ change | number:'1.1-2' }} BMI vs last</span>
      </div>
  </div>
</div>
